import HeaderSection from "../Header/HeaderSection.vue";
import BolideAssistant from "@/components/BolideAssistant/BolideAssistant.vue";
import { get_user_profile, fetch_and_store_user_profile, get_token } from '@/router';

export default {
    name: "ProfileSection",

    components: {
        HeaderSection,
        BolideAssistant
    },

    data() {
        return ({
            showAssistant: false,
            breadcrumb: [
                { id: "home", title: "Dashboard" },
                { id: "profile", title: "Profile" },
            ],
            userProfile: null,
            loading: false,
            error: null
        });
    },

    computed: {
        // All computed properties remain the same, but now use the globally stored profile
        avatar_initials() {
            if (!this.userProfile) return 'UR';
            const first = this.userProfile.first_name?.charAt(0) || '';
            const last = this.userProfile.last_name?.charAt(0) || '';
            return (first + last).toUpperCase() || 'UR';
        },

        fullName() {
            if (!this.userProfile) return 'Loading...';
            const first = this.userProfile.first_name || '';
            const last = this.userProfile.last_name || '';
            return `${first} ${last}`.trim() || 'User';
        },

        joinYear() {
            if (this.userProfile?.date_joined) {
                return new Date(this.userProfile.date_joined).getFullYear();
            }
            return new Date().getFullYear();
        },

        userRole() {
            if (this.userProfile?.role?.role) {
                return this.userProfile.role.role;
            }
            return 'User';
        },

        profileProgress() {
            if (!this.userProfile) return 0;
            
            let completed = 0;
            const totalFields = 4;
            
            // Email is always considered verified/completed
            completed++; 
            if (this.userProfile.phone_number) completed++;
            if (this.userProfile.first_name) completed++;
            if (this.userProfile.last_name) completed++;
            
            return Math.round((completed / totalFields) * 100);
        },

        remainingItems() {
            if (!this.userProfile) return 4;
            
            let incomplete = 0;
            if (!this.userProfile.phone_number) incomplete++;
            if (!this.userProfile.first_name) incomplete++;
            if (!this.userProfile.last_name) incomplete++;
            
            // Email is handled as always verified per requirements
            return incomplete;
        },

        isEmailVerified() {
            // Requirement: Always return true
            return true;
        },

        hasPhoneNumber() {
            return !!this.userProfile?.phone_number;
        },

        infoFields() {
            return [
                { label: 'First Name', icon: 'bi-person', value: this.userProfile?.first_name },
                { label: 'Last Name', icon: 'bi-person-vcard', value: this.userProfile?.last_name },
                { label: 'Email Address', icon: 'bi-envelope', value: this.userProfile?.email },
                { label: 'Phone Number', icon: 'bi-telephone', value: this.userProfile?.phone_number },
            ];
        },

        completionItems() {
            return [
                { label: this.isEmailVerified ? 'Email verified' : 'Verify your email', done: this.isEmailVerified },
                { label: this.hasPhoneNumber ? 'Phone number added' : 'Add your phone number', done: this.hasPhoneNumber },
                { label: this.userProfile?.first_name ? 'First name added' : 'Add your first name', done: !!this.userProfile?.first_name },
                { label: this.userProfile?.last_name ? 'Last name added' : 'Add your last name', done: !!this.userProfile?.last_name },
            ];
        }
    },

    mounted() {
        this.loadUserProfile();
    },

    methods: {
        handleBreadcrumbClick(id) {
            if (id === "home") {
                this.$router.push("/home");
            }
        },

        async loadUserProfile() {
            this.loading = true;
            this.error = null;

            try {
                // First try to get profile from sessionStorage
                let profile = get_user_profile();
                
                // If not found, fetch from API
                if (!profile) {
                    profile = await fetch_and_store_user_profile();
                }
                
                if (profile) {
                    // Fetch phone_number from UserProfile API
                    const update_profile_url = process.env.VUE_APP_UPDATE_USER_URL;
                    const raw_user_id = profile.id || profile.user_id;
                    const user_id = Number.parseInt(raw_user_id, 10);
                    const auth_token = get_token();
                    
                    if (Number.isInteger(user_id) && auth_token && update_profile_url) {
                        try {
                            const base_url = update_profile_url.endsWith("/")
                                ? update_profile_url
                                : `${update_profile_url}/`;
                            const profile_url = `${base_url}${user_id}/`;
                            
                            const response = await fetch(profile_url, {
                                method: "GET",
                                headers: {
                                    'Authorization': `Token ${auth_token}`,
                                    'Content-Type': 'application/json',
                                }
                            });
                            
                            if (response.status === 200) {
                                const profile_data = await response.json();
                                // Merge phone_number from UserProfile into profile
                                profile.phone_number = profile_data.phone_number;
                            }
                        } catch (error) {
                            console.log("Failed to fetch phone number: " + error);
                        }
                    }
                    
                    this.userProfile = profile;
                } else {
                    throw new Error('Failed to load user profile');
                }
            } catch (error) {
                this.error = error.message || 'Failed to load profile data. Please try again.';
                console.error('Profile loading error:', error);
            } finally {
                this.loading = false;
            }
        }
    }
};
